import { create } from 'zustand';
import { api } from '../services/apiClient';
import { useAuth } from './authStore';

// Sprint-12 — first-run Dashboard tour (3 steps).
//
// State + persistence model:
//
//   • The authoritative state lives in _worksnip_users (columns
//     onboarding_completed_at + onboarding_progress, see migration 016).
//     Every dismiss / complete / reset POSTs to /api/v1/me/onboarding/*
//     and then patches useAuth.user in-place so the rest of the app
//     reads consistent values without a /auth/me round-trip.
//
//   • This Zustand store is a derived in-memory view: which step is
//     currently visible, which steps the user has already cleared in
//     this session. It hydrates from the authStore's user.onboarding_*
//     on first render via `hydrateFromUser(user)`.
//
//   • localStorage is intentionally NOT used. The backend round-trip is
//     cheap (one tiny POST per dismiss) and using only the backend means
//     "user clears storage" or "user signs in on a new device" both
//     pick up the tour exactly where they left off — see the report
//     scenario at the end of the task brief.

export type OnboardingStepKey = 'extension' | 'capture' | 'share';
export const ONBOARDING_STEP_ORDER: OnboardingStepKey[] = ['extension', 'capture', 'share'];

interface OnboardingProgressEntry {
  dismissed_at?: string;
  seen?: boolean;
}
type OnboardingProgress = Partial<Record<OnboardingStepKey, OnboardingProgressEntry>>;

interface OnboardingState {
  /** Hydration flag — true after we've consumed user.onboarding_* once. */
  hydrated: boolean;
  /** Backend authoritative timestamp. null → tour active. */
  completedAt: string | null;
  /** Per-step state mirrored from the server. */
  progress: OnboardingProgress;
  /** Which step is currently visible (0-based) — null when tour is closed. */
  currentStepIndex: number | null;
  /** Hydrate from a freshly-fetched user record (login or /auth/me). */
  hydrateFromUser: (user: { onboarding_completed_at?: string | null; onboarding_progress?: OnboardingProgress | null } | null) => void;
  /** Move forward by one step. If past the last step, completes the tour. */
  next: () => Promise<void>;
  /** Dismiss a single step (does not complete the tour). */
  dismissStep: (step: OnboardingStepKey) => Promise<void>;
  /** Mark the entire tour completed (Skip all / finished step 3). */
  completeAll: () => Promise<void>;
  /** Restart tour — clears server state and re-opens at step 0. */
  reset: () => Promise<void>;
}

// Helper — find the first step whose dismissed_at is not set. Used on
// hydration so a user who already completed steps 1+2 reopens at step 3.
function firstActiveIndex(progress: OnboardingProgress): number {
  for (let i = 0; i < ONBOARDING_STEP_ORDER.length; i++) {
    const key = ONBOARDING_STEP_ORDER[i];
    if (!progress[key]?.dismissed_at) return i;
  }
  return 0;
}

// Mutate useAuth.user in place after a server write. We avoid replacing
// the entire user object because other components hold references to
// per-field selectors — patching the relevant two keys preserves
// reference equality everywhere else.
function patchAuthUserOnboarding(patch: { onboarding_completed_at: string | null; onboarding_progress: OnboardingProgress }) {
  const { user, organization, token, setAuth } = useAuth.getState();
  if (!user || !token) return;
  setAuth({
    token,
    organization,
    user: { ...user, ...patch },
  });
}

export const useOnboarding = create<OnboardingState>((set, get) => ({
  hydrated: false,
  completedAt: null,
  progress: {},
  currentStepIndex: 0,

  hydrateFromUser: (user) => {
    // Re-hydration is idempotent: every authStore change re-runs this,
    // and we only flip currentStepIndex on the FIRST run (so user
    // navigation between steps in a session isn't blown away by an
    // unrelated user-record refresh).
    const completedAt   = user?.onboarding_completed_at ?? null;
    const progress      = user?.onboarding_progress ?? {};
    const alreadyHydrated = get().hydrated;
    set({
      hydrated: true,
      completedAt,
      progress,
      currentStepIndex: alreadyHydrated
        ? get().currentStepIndex
        : (completedAt ? null : firstActiveIndex(progress)),
    });
  },

  next: async () => {
    const idx = get().currentStepIndex;
    if (idx === null) return;
    const currentKey = ONBOARDING_STEP_ORDER[idx];
    if (!currentKey) return;
    // Persist this step as dismissed/seen before advancing — so a
    // mid-tour sign-out leaves a coherent server record.
    await get().dismissStep(currentKey);
    const nextIdx = idx + 1;
    if (nextIdx >= ONBOARDING_STEP_ORDER.length) {
      await get().completeAll();
    } else {
      set({ currentStepIndex: nextIdx });
    }
  },

  dismissStep: async (step) => {
    const res = await api.post('/api/v1/me/onboarding/dismiss', { step });
    const data = res.data as { onboarding_completed_at: string | null; onboarding_progress: OnboardingProgress };
    set({
      completedAt: data.onboarding_completed_at,
      progress:    data.onboarding_progress || {},
    });
    patchAuthUserOnboarding({
      onboarding_completed_at: data.onboarding_completed_at,
      onboarding_progress:     data.onboarding_progress || {},
    });
  },

  completeAll: async () => {
    const res = await api.post('/api/v1/me/onboarding/dismiss', { step: 'all' });
    const data = res.data as { onboarding_completed_at: string | null; onboarding_progress: OnboardingProgress };
    set({
      completedAt: data.onboarding_completed_at,
      progress:    data.onboarding_progress || {},
      currentStepIndex: null,
    });
    patchAuthUserOnboarding({
      onboarding_completed_at: data.onboarding_completed_at,
      onboarding_progress:     data.onboarding_progress || {},
    });
  },

  reset: async () => {
    const res = await api.post('/api/v1/me/onboarding/reset', {});
    const data = res.data as { onboarding_completed_at: string | null; onboarding_progress: OnboardingProgress };
    set({
      completedAt: null,
      progress:    {},
      currentStepIndex: 0,
    });
    patchAuthUserOnboarding({
      onboarding_completed_at: data.onboarding_completed_at,
      onboarding_progress:     data.onboarding_progress || {},
    });
  },
}));
